import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import api from "../../api/axios";

export default function VendorStorePolicy() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState({
    returnPolicy: "",
    shippingPolicy: "",
    warranty: "",
    processingTime: "1_day",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success"); // success or error

  // Load policies on component mount
  useEffect(() => {
    const fetchPolicies = async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/vendor/policies");

        setPolicies({
          returnPolicy: data.policies?.returnPolicy || "",
          shippingPolicy: data.policies?.shippingPolicy || "",
          warranty: data.policies?.warranty || "",
          processingTime: data.policies?.processingTime || "1_day",
        });
      } catch (err) {
        console.error("Failed to fetch policies", err);
        setMessage("Failed to load policies");
        setMessageType("error");
      } finally {
        setLoading(false);
      }
    };

    fetchPolicies();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage("");
      const { data } = await api.patch("/vendor/policies", policies);

      setMessage("Policies saved successfully");
      setMessageType("success");
    } catch (err) {
      console.error(err);
      setMessage(err?.response?.data?.message || "Failed to save policies");
      setMessageType("error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 2 }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/vendor/dashboard"
            onClick={(e) => {
              e.preventDefault();
              navigate("/vendor/dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Vendor Dashboard
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            Store Policies
          </Typography>
        </Breadcrumbs>
      </Box>

      <Typography variant="h5" fontWeight={700} mb={3}>
        Store Policies
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 2 }} onClose={() => setMessage("")}>
          {message}
        </Alert>
      )}

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        {/* RETURN POLICY */}
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight={600} mb={1}>
            Return Policy
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={policies.returnPolicy}
            onChange={(e) =>
              setPolicies({ ...policies, returnPolicy: e.target.value })
            }
            placeholder="e.g. 7-day return available. Product must be unused."
            variant="outlined"
          />
        </Box>

        {/* SHIPPING POLICY */}
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight={600} mb={1}>
            Shipping Policy
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={policies.shippingPolicy}
            onChange={(e) =>
              setPolicies({ ...policies, shippingPolicy: e.target.value })
            }
            placeholder="e.g. Ships within 1-2 days"
            variant="outlined"
          />
        </Box>

        {/* WARRANTY */}
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight={600} mb={1}>
            Warranty
          </Typography>
          <TextField
            fullWidth
            value={policies.warranty}
            onChange={(e) =>
              setPolicies({ ...policies, warranty: e.target.value })
            }
            placeholder="e.g. 1 year manufacturer warranty"
            variant="outlined"
          />
        </Box>

        {/* PROCESSING TIME */}
        <Box sx={{ mb: 3 }}>
          <Typography fontWeight={600} mb={1}>
            Processing Time
          </Typography>
          <Select
            fullWidth
            value={policies.processingTime}
            onChange={(e) =>
              setPolicies({ ...policies, processingTime: e.target.value })
            }
            variant="outlined"
          >
            <MenuItem value="same_day">Same Day</MenuItem>
            <MenuItem value="1_day">1 Day</MenuItem>
            <MenuItem value="2_days">2 Days</MenuItem>
            <MenuItem value="3_5_days">3-5 Days</MenuItem>
            <MenuItem value="1_week">1 Week</MenuItem>
          </Select>
        </Box>

        {/* SAVE BUTTON */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: "#ffa41c",
              color: "#111",
              fontWeight: 600,
              "&:hover": { bgcolor: "#f90" },
              "&:disabled": { bgcolor: "#ccc" },
            }}
          >
            {saving ? "Saving..." : "Save Policies"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
